import React, { useEffect, useState, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { setLogout, updateToken } from "../../state/index.js";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  ChakraProvider,
  Container,
  Flex,
  Heading,
  VStack,
  Text,
  extendTheme,
  useColorMode,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  ProfileUser,
  LynxId,
  DeleteAccount,
} from "../profilePage/profileApi.js";
import { MdMoreVert } from "react-icons/md";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import ErrorAlert from "../../components/ErrorAlert";
import SuccessAlert from "../../components/SuccessAlert.js";
import logo from "../../res/secureIcon.png";
import { CDN_URL, DEFAULT_PROFILE_PIC_URL } from "../../utils/API_CONSTANTS";
/*
const dummyProfileData = {
  name: "abddcvmpwmfkewm",
  roll_no: "108123142",
  department: "Electronics and Communication Engineering",
  major: "B.Tech - Electronics and Communication Engineering",
  email: null,
  profile_pic: "https://bit.ly/dan-abramov",
};
const dummyLynxId = {
  id: "LynxID-12345678",
};
*/
const ProfileScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const token = useSelector((state) => state.auth.token);

  const [profileData, setProfileData] = useState(null);
  const [lynxId, setLynxId] = useState(null);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true); // Loading state
  const theme = extendTheme({
    shadows: {
      light: "0 0 5px white",
    },
    breakpoints: {
      sm: "350px",
    },
  });

  const onLogout = useCallback(() => {
    dispatch(setLogout());
    navigate("/login");
  }, [dispatch, navigate]);

  const onDeleteAccount = useCallback(async () => {
    if (!token) {
      setError("No token found. Please log in again.");
      return;
    }

    try {
      await DeleteAccount(token);
      setSuccessMessage("Account Deleted Successfully.");
      setIsModalOpen(false);
      navigate("/register-rollNo");
    } catch (err) {
      setError(`Account Deletion Failed`);
    }
  }, [token]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token) {
        setError("No token found. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        const data = await ProfileUser(token);
        setProfileData(data.details);
        dispatch(updateToken(token));
      } catch (err) {
        // console.error("Failed to fetch profile data. Using dummy data.", err);
        /*setProfileData(dummyProfileData);*/

        if (!navigator.onLine) {
          setError(`No Internet Connection`);
        } else {
          setError(`Server Error , Please try again later!`);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchLynxId = async () => {
      if (!token) {
        setError("No token found. Please log in again.");
        return;
      }

      try {
        const data = await LynxId(token);
        setLynxId(data);
        dispatch(updateToken(token));
      } catch (err) {
        // console.error("Failed to fetch Lynx ID. Using dummy data.", err);
        /* setLynxId(dummyLynxId);*/
        if (!navigator.onLine) {
          setError(`No Internet Connection`);
        } else {
          setError(`Server error`);
        }
      }
    };

    fetchUserProfile();
    // fetchLynxId();
  }, [token, dispatch]);

  if (loading) {
    return (
      <Center
        height="100vh"
        width="100%"
        bg={colorMode === "dark" ? "gray.800" : "gray.100"}
      >
        <Spinner
          thickness="15px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="l"
        />
      </Center>
    );
  }

  if (error && error === "Server Error , Please try again later!") {
    return <ErrorAlert message={error} onClose={() => setError(null)} />;
  }
  const { name, roll_no, department, email, profile_pic } = profileData;

  return (
    <ChakraProvider theme={theme}>
      <Menu>
        <MenuButton
          as={IconButton}
          aria-label="Options"
          icon={<MdMoreVert />}
          variant="ghost"
          color={colorMode === "light" ? "blue.600" : "yellow.400"}
          size="lg"
          ml={2}
          fontSize="2xl"
          borderRadius={"5"}
          width={"fit-content"}
        />
        <MenuList>
          <MenuItem onClick={onLogout}>Logout</MenuItem>
          <MenuItem onClick={() => setIsModalOpen(true)} color="red.500">
            Delete Account
          </MenuItem>
        </MenuList>
      </Menu>

      <Flex
        height="85%"
        marginTop={2}
        direction="column"
        width={"85%"}
        ml={"7.5%"}
        mr={"7.5%"}
      >
        <Center h="full">
          <Container
            maxW="sm"
            bg={colorMode === "dark" ? "gray.700" : "white"}
            borderRadius="lg"
            p={2}
            boxShadow={colorMode === "light" ? "dark-lg" : "light"}
          >
            <VStack spacing={4} align="stretch">
              <Heading
                as="h1"
                size="lg"
                textAlign="center"
                color={"white"}
                bg={"blue.600"}
                py={1}
                borderRadius={5}
              >
                Lynx ID
              </Heading>
              <Card>
                <CardBody>
                  <VStack spacing={2}>
                    <Avatar
                      src={
                        profile_pic && profile_pic.trim()
                          ? `${CDN_URL}/${profile_pic}`
                          : DEFAULT_PROFILE_PIC_URL
                      }
                      name={name}
                      size="2xl"
                    />
                    <Text fontSize="2xl" fontWeight="bold" textAlign={"center"}>
                      {name}
                    </Text>
                    <Text textAlign={"center"} size={"xs"}>
                      {roll_no}
                    </Text>
                    <VStack spacing={2} align="stretch" width={"full"}>
                      <Flex justify={"space-between"} width="full">
                        <Text textAlign={"left"} size={"xs"} fontWeight="bold">
                          Major:
                        </Text>
                        <Text textAlign={"right"} size={"xs"}>
                          {department || ""}
                        </Text>
                      </Flex>
                      <Flex justify={"space-between"} width="full">
                        <Text textAlign={"left"} size={"xs"} fontWeight="bold">
                          Email:
                        </Text>
                        <Text textAlign={"right"} size={"xs"}>
                          {email || `${roll_no}@nitt.edu`}
                        </Text>
                      </Flex>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
              <Button
                color={"black"}
                onClick={() => navigate("/security")}
                border={"2px solid black"}
                py={4}
                bg={"white"}
                _hover={{ bg: "grey" }}
              >
                <Image
                  alt="Spider Secure"
                  src={logo}
                  boxSize={{ base: "30px", sm: "35px", md: "45px" }}
                  objectFit="contain"
                  mr={"2px"}
                />
                Lynx Authenticator
              </Button>
              <Button
                as="h1"
                size="lg"
                textAlign="center"
                color={"white"}
                bg={"blue.600"}
                py={1}
                borderRadius={5}
                _hover={{ bg: "blue.700" }}
                onClick={() => navigate("/lynxid")}
              >
                Lynx ID
              </Button>
              {lynxId && (
                <Flex justifyContent="center">
                  <Box
                    textAlign="center"
                    size="2xl"
                    w="100%"
                    p={4}
                    borderRadius={5}
                    bg={"white"}
                    border={"1px solid black"}
                  >
                    <QRCodeCanvas
                      value={lynxId.id || ""}
                      size={"250"}
                      style={{ width: "100%", height: "auto" }}
                    />
                  </Box>
                </Flex>
              )}
              <Text fontSize="xs" textAlign="center">
                driven by SpiderSecure
              </Text>
            </VStack>
          </Container>
        </Center>
      </Flex>
      <DeleteConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={onDeleteAccount}
      />
      <SuccessAlert
        message={successMessage}
        onClose={() => setSuccessMessage("")}
      />
      <ErrorAlert message={error} onClose={() => setError(null)} />
    </ChakraProvider>
  );
};

export default ProfileScreen;
